import GoalIcon from '@atlaskit/icon/core/goal';
import HomeIcon from '@atlaskit/icon/core/home';
import ProjectIcon from '@atlaskit/icon/core/project';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { token } from '@atlaskit/tokens';
import React, { Suspense } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl-next';
import { graphql, PreloadedQuery, useLazyLoadQuery, usePreloadedQuery } from 'react-relay';
import { RouteContext, useRouter } from 'react-resource-router';
import styled from 'styled-components';

import { YOUR_WORK_PATH } from '@townsquare/config/routes';
import { ViewIcon } from '@townsquare/directory-header';
import ProjectIconLegacy from '@townsquare/ds-icon/glyph/atlas/project';
import HomeIconLegacy from '@townsquare/ds-icon/glyph/home';
import { filterUndefined } from '@townsquare/filter-type';
import {
  goalUpdateViewRoute,
  projectUpdateViewRoute,
  savedViewsUpdatesRoute,
  tagUUIDRedirectRoute,
  teamUpdatesViewRoute,
} from '@townsquare/home-view/routes';
import { homeTabRoute } from '@townsquare/hometab-view';
import { BaseGoalIcon, TagHashIcon } from '@townsquare/icons';
import { LoadingSkeleton, LoadingSkeletonContainer, SquareLoadingSkeleton } from '@townsquare/loading-skeleton';
import { FullPageViewErrorBoundary } from '@townsquare/monitoring';
import { useRelayResource } from '@townsquare/relay-utils';
import { generatePath, useGeneratePath, useRouteDependencies } from '@townsquare/router/primitives';
import { SidebarNavigation, SideNavItem, SideNavSection } from '@townsquare/sidebar-navigation';
import { TeamIcon } from '@townsquare/team-icon';
import { useWorkspaceStore } from '@townsquare/workspace-store';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';
import { useWorkspaceType } from '@townsquare/workspace-store/hooks';

import { homebarSideNavigationResource } from '../resources/homebarSideNavigationResource';

import { NoWorkspaceHomeSidebarNavigation } from './NoWorkspaceHomeSidebarNavigation';
import { HomeSidebarNavigationQuery } from './__generated__/HomeSidebarNavigationQuery.graphql';
import { HomeSidebarNavigation_currentItemQuery } from './__generated__/HomeSidebarNavigation_currentItemQuery.graphql';

export type HomeSidebarNavigationWrapperProps = RouteContext & {
  isHome: boolean;
};

const useBaseUpdatesNavItems = (intl: IntlShape, workspaceType: ReturnType<typeof useWorkspaceType>): SideNavItem[] => {
  const projectUpdatesPath = useGeneratePath(projectUpdateViewRoute);
  const goalUpdatesPath = useGeneratePath(goalUpdateViewRoute);
  const hometabPath = useGeneratePath(homeTabRoute);

  const navItems = [
    {
      key: YOUR_WORK_PATH,
      path: YOUR_WORK_PATH,
      name: intl.formatMessage({
        id: 'townsquare.packages.home-layout.sidebar-navigation-your-work',
        defaultMessage: 'Your work',
        description: 'Label for the Your work sidebar navigation item',
      }),
      testId: 'side-nav-your-work',
      icon: <HomeIcon label="" LEGACY_fallbackIcon={HomeIconLegacy} />,
    },
    {
      key: hometabPath,
      path: hometabPath,
      // matches: [hometabPath],
      name: intl.formatMessage({
        id: 'townsquare.packages.home-layout.sidebar-navigation-home',
        defaultMessage: 'Home',
        description: 'Label for the Your work sidebar navigation item',
      }),
      testId: 'side-nav-home',
      icon: <HomeIcon label="" LEGACY_fallbackIcon={HomeIconLegacy} />,
    },
    {
      key: projectUpdatesPath,
      path: projectUpdatesPath,
      matches: [projectUpdatesPath],
      name: intl.formatMessage({
        id: 'townsquare.packages.home-layout.sidebar-navigation-projects',
        defaultMessage: 'Project updates',
        description: 'Label for the Project updates sidebar navigation item',
      }),
      testId: 'side-nav-project-updates',
      icon: <ProjectIcon label="" LEGACY_fallbackIcon={ProjectIconLegacy} />,
    },
    {
      key: goalUpdatesPath,
      path: goalUpdatesPath,
      matches: [goalUpdatesPath],
      name: intl.formatMessage({
        id: 'townsquare.packages.home-layout.sidebar-navigation-goals',
        defaultMessage: 'Goal updates',
        description: 'Label for the Goals sidebar navigation item',
      }),
      testId: 'side-nav-goal-updates',
      icon: <GoalIcon label="" LEGACY_fallbackIcon={() => <BaseGoalIcon label="" />} />,
    },
  ];

  return navItems.filter(item => {
    if (item.key === YOUR_WORK_PATH && workspaceType === 'GLOBAL_EXPERIENCE') {
      return false;
    } else if (item.key === hometabPath && workspaceType === 'PRODUCT') {
      return false;
    }

    return true;
  });
};

const ErrorStateContainer = styled.div({
  alignItems: 'center',
  display: 'flex',
  marginLeft: token('space.100'),
  gap: token('space.150'),
  height: '32px',
});

const createErrorState = (): SideNavSection => {
  return {
    title: 'Update feeds',
    customState: (
      <ErrorStateContainer>
        <ErrorIcon label="" />
        <FormattedMessage
          id="townsquare.packages.home-layout.sidebar-navigation-error"
          defaultMessage="Couldn't load list"
          description="Error message for the sidebar navigation"
        />
      </ErrorStateContainer>
    ),
    items: [],
  };
};

const createLoadingState = (): SideNavSection => {
  return {
    customState: (
      <LoadingSkeletonContainer margin="44px 10px 10px">
        <SquareLoadingSkeleton sideLengthPx={24} marginRightPx={16} />
        <LoadingSkeleton height="16px" width="124px" marginRightPx={10} />
      </LoadingSkeletonContainer>
    ),
    items: [],
  };
};

const createTeamSideNavItem = (name: string, path: string): SideNavItem => ({
  key: path,
  path: path,
  matches: [path],
  name: name,
  analyticsName: 'Team feed',
  icon: <TeamIcon teamName={name} size="small" />,
});

const createTagSideNavItem = (name: string, path: string): SideNavItem => ({
  key: path,
  path: path,
  matches: [path],
  name: name,
  analyticsName: 'Tag feed',
  testId: `tag-menu-item-${name}`,
  icon: <TagHashIcon tagName={name} size={24} />,
});

const createSavedViewSideNavItem = (name: string, path: string, uuid: string): SideNavItem => ({
  key: path,
  path: path,
  matches: [path],
  name,
  analyticsName: 'SavedView feed',
  testId: `saved-view-menu-item-${uuid}`,
  icon: <ViewIcon uuid={uuid} size="small" />,
});

export const HomeSidebarNavSkeleton = ({ extraSection }: { extraSection: SideNavSection }) => {
  const [routerStore] = useRouter();
  const workspaceType = useWorkspaceType();
  const intl = useIntl();
  const items = useBaseUpdatesNavItems(intl, workspaceType);

  return (
    <SidebarNavigation
      label={intl.formatMessage({
        id: 'townsquare.packages.home-layout.sidebar-navigation',
        defaultMessage: 'Updates',
        description: 'Label for the sidebar navigation',
      })}
      testId="updates-side-nav"
      sections={[{ items }, extraSection]}
      selectedKey={routerStore.match.url}
    />
  );
};

export const HomeSidebarNavigation = (
  props: RouteContext & { queryRef: PreloadedQuery<HomeSidebarNavigationQuery> },
) => {
  const [routerStore] = useRouter();
  const [workspace] = useWorkspaceStore();
  const intl = useIntl();

  const watchedData = usePreloadedQuery<HomeSidebarNavigationQuery>(
    graphql`
      query HomeSidebarNavigationQuery(
        $workspaceId: ID
        $workspaceUuid: UUID!
        $savedViewTql: String
        $cloudId: String
      ) {
        currentUser {
          id
          tagsWatching(workspaceId: $workspaceId, first: 200) @connection(key: "CurrentUser__tagsWatching") {
            edges {
              node {
                id
                uuid
                name
              }
            }
          }
          teamsWatching(cloudId: $cloudId) {
            edges {
              node {
                id
                teamId
                name
              }
            }
          }
        }

        directoryViewTql(q: $savedViewTql, first: 200, workspaceUuid: $workspaceUuid, sort: [FAVOURITED_DATE_DESC])
          @connection(key: "HomeSidebarNavigationQuery_directoryViewTql", filters: ["workspaceUuid"]) {
          edges {
            node {
              uuid
              name
            }
          }
        }
      }
    `,
    props.queryRef,
  );

  const currentItemData = useLazyLoadQuery<HomeSidebarNavigation_currentItemQuery>(
    graphql`
      query HomeSidebarNavigation_currentItemQuery(
        $teamId: String!
        $tagUuid: UUID!
        $viewUuid: UUID!
        $workspaceUuid: UUID!
        $cloudId: String
      ) {
        teamByTeamId(teamId: $teamId, cloudId: $cloudId) {
          name
          teamId
        }

        tagByUuid(uuid: $tagUuid) {
          name
          uuid
        }

        view: directoryViewByUuid(workspaceUuid: $workspaceUuid, uuid: $viewUuid) {
          uuid
          name
        }
      }
    `,
    {
      teamId: props.match.params.id || '',
      tagUuid: props.match.params.id || '',
      viewUuid: props.match.params.uuid || '',
      workspaceUuid: workspace.UUID,
      cloudId: workspace.cloudId,
    },
    { fetchPolicy: 'store-only' },
  );

  const { orgId, cloudId, workspaceType } = useRouteDependencies();
  const teamPath = useGeneratePath(teamUpdatesViewRoute, { id: currentItemData?.teamByTeamId?.teamId ?? 'id' });
  const tagPath = useGeneratePath(tagUUIDRedirectRoute, { id: currentItemData?.tagByUuid?.uuid ?? 'id' });
  const savedViewPath = useGeneratePath(savedViewsUpdatesRoute, { uuid: currentItemData?.view?.uuid ?? 'id' });

  let currentNavItem: SideNavItem | null = null;
  if (currentItemData?.teamByTeamId?.name) {
    currentNavItem = createTeamSideNavItem(currentItemData.teamByTeamId.name, teamPath);
  } else if (currentItemData.tagByUuid?.name) {
    currentNavItem = createTagSideNavItem(currentItemData.tagByUuid.name, tagPath);
  } else if (currentItemData.view?.name && currentItemData.view.uuid) {
    currentNavItem = createSavedViewSideNavItem(currentItemData.view.name, savedViewPath, currentItemData.view.uuid);
  }

  const tagEdges = watchedData.currentUser?.tagsWatching?.edges;
  const teamEdges = watchedData.currentUser?.teamsWatching?.edges;
  const viewEdges = watchedData.directoryViewTql?.edges;

  const tagsWatched: SideNavItem[] =
    (tagEdges &&
      tagEdges
        .map(tag => {
          if (!tag?.node || !tag.node.name || !tag.node.uuid || !tag.node.id) {
            return;
          }

          const path = generatePath(tagUUIDRedirectRoute, {
            baseParams: { id: tag.node.uuid },
            orgId,
            cloudId,
            workspaceType,
          });

          return createTagSideNavItem(tag.node.name, path);
        })
        .filter(filterUndefined)) ||
    [];

  const teamsWatched: SideNavItem[] =
    (teamEdges &&
      teamEdges
        .map(team => {
          if (!team?.node || !team.node.name || !team.node.teamId) {
            return;
          }

          const path = generatePath(teamUpdatesViewRoute, {
            baseParams: { id: team.node.teamId },
            orgId,
            cloudId,
            workspaceType,
          });

          return createTeamSideNavItem(team.node.name, path);
        })
        .filter(filterUndefined)) ||
    [];

  const viewsStarred: SideNavItem[] =
    (viewEdges &&
      viewEdges
        .map(view => {
          if (!view?.node || !view.node.name || !view.node.uuid) {
            return;
          }

          const path = generatePath(savedViewsUpdatesRoute, {
            baseParams: { uuid: view.node.uuid },
            orgId,
            cloudId,
            workspaceType,
          });
          return createSavedViewSideNavItem(view.node.name, path, view.node.uuid);
        })
        .filter(filterUndefined)) ||
    [];

  const mergedTeamsAndTags: SideNavItem[] = [...teamsWatched, ...tagsWatched, ...viewsStarred];

  if (currentNavItem && !mergedTeamsAndTags.find(navItem => navItem.key === currentNavItem?.key)) {
    mergedTeamsAndTags.unshift(currentNavItem);
  }

  const tagsAndTeamSection = {
    testId: 'topic-update-menu-items',
    items: mergedTeamsAndTags,
  };

  const updatesNavItems = useBaseUpdatesNavItems(intl, workspaceType);

  const sections = [{ items: updatesNavItems }, tagsAndTeamSection];

  return (
    <SidebarNavigation
      label={intl.formatMessage({
        id: 'townsquare.packages.home-layout.sidebar-navigation',
        defaultMessage: 'Updates',
        description: 'Label for the sidebar navigation',
      })}
      testId="updates-side-nav"
      sections={sections}
      selectedKey={routerStore.match.url}
    />
  );
};

export const HomeSidebarNavigationWrapper = (props: HomeSidebarNavigationWrapperProps) => {
  const queryRef = useRelayResource(homebarSideNavigationResource);
  const noWorkspaceExperience = useNoWorkspaceExperience();

  if (!queryRef) {
    return null;
  }

  return (
    <FullPageViewErrorBoundary FallbackComponent={() => <HomeSidebarNavSkeleton extraSection={createErrorState()} />}>
      <Suspense fallback={<HomeSidebarNavSkeleton extraSection={createLoadingState()} />}>
        {noWorkspaceExperience ? (
          <NoWorkspaceHomeSidebarNavigation isHome={props.isHome} />
        ) : (
          <HomeSidebarNavigation {...props} queryRef={queryRef} />
        )}
      </Suspense>
    </FullPageViewErrorBoundary>
  );
};
